import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, Headphones, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SIGNIN_URL } from "@/data/site";
import { OFundedLogo } from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { prefetch, prefetchProps } from "@/lib/prefetch";
import { MegaRibbon } from "@/components/Navbar.MegaMenu";
import { useTranslatedMegaMenu } from "@/data/megaMenuI18n";
import { pushOverlay, popOverlay } from "@/lib/overlayState";
import InlineSearch from "@/components/InlineSearch";
import NavbarMobileMenu from "@/components/NavbarMobileMenu";
import { useCtaTarget } from "@/lib/useCtaTarget";

/* Utility links surfaced in the top tier · these are the "always
 * reachable from anywhere" links that used to be buried 2-3 clicks
 * deep inside the Company mega menu. */
const UTILITY_LINKS = [
  { to: "/contact", external: false, Icon: Mail,       labelKey: "nav.contactUs",  fallback: "Contact Us"  },
  { to: "/support", external: false, Icon: Headphones, labelKey: "nav.helpCenter", fallback: "Help Center" },
];

export default function Navbar() {
  const { t } = useTranslation();
  // Per-location CTA routing · the header desktop button + the
  // mobile-menu sticky CTA each read their own admin setting. Defaults
  // to wizard for the desktop button and direct CRM for mobile; the
  // global wizard kill-switch forces both back to CRM when OFF.
  const headerCta = useCtaTarget("navbar_desktop");
  const mobileCta = useCtaTarget("navbar_mobile");
  const openAccountHref      = headerCta.href;
  const openAccountIsExternal = headerCta.isExternal;
  const MEGA_MENU_T = useTranslatedMegaMenu();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [searchOpenSignal, setSearchOpenSignal] = useState(0);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset menu/overlay state on URL change · the canonical React
  // pattern for syncing ephemeral UI state to the router. Newer
  // experimental "set-state-in-effect" rules flag this, but the
  // build-time ESLint (react-hooks@4) ships clean.
  useEffect(() => {
    setOpen(false);
    setActiveKey(null);
  }, [location.pathname]);

  // Close mega-menu on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Global search shortcuts · ⌘K / Ctrl+K bump a signal that
  // InlineSearch listens to so the expanded input gets focused. "/"
  // does the same as long as the user isn't typing in another input.
  useEffect(() => {
    const onSearchKey = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpenSignal((s) => s + 1);
        return;
      }
      if (e.key === "/" && !cmd) {
        const tag = (e.target?.tagName || "").toUpperCase();
        const isEditable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          setSearchOpenSignal((s) => s + 1);
        }
      }
    };
    window.addEventListener("keydown", onSearchKey);
    return () => window.removeEventListener("keydown", onSearchKey);
  }, []);

  // Click-outside to close the mega-menu · solves the "menu kept
  // disappearing when I tried to move my cursor" frustration. We
  // attach a window pointerdown listener while the menu is open and
  // close it as soon as the user clicks outside the header.
  useEffect(() => {
    if (!activeKey) return;
    const onDocDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setActiveKey(null);
      }
    };
    document.addEventListener("pointerdown", onDocDown);
    return () => document.removeEventListener("pointerdown", onDocDown);
  }, [activeKey]);

  const scheduleClose = () => {
    // No-op now · we only close on explicit click-outside or ESC.
    // (Previously this fired a 220ms timeout on mouseleave which made
    // the menu feel like it had a mind of its own.)
  };
  const cancelClose = () => {};
  const openSection = (key) => {
    setActiveKey(key);
    // Eagerly warm chunks for every link in this section so the
    // upcoming click feels instant.
    const entry = MEGA_MENU_T.find((e) => e.key === key);
    if (entry) {
      for (const col of entry.columns) {
        for (const it of col.items) prefetch(it.to.split("?")[0]);
      }
      if (entry.spotlight?.cta?.to) prefetch(entry.spotlight.cta.to.split("?")[0]);
    }
  };

  // Lock body scroll + hide Tawk while the full-screen mobile overlay
  // is open. Tawk's launcher iframe has a z-index of ~1e6 so it would
  // otherwise sit on top of the menu.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    pushOverlay();
    return () => {
      document.body.style.overflow = prev;
      popOverlay();
    };
  }, [open]);

  const activeEntry = activeKey ? MEGA_MENU_T.find((e) => e.key === activeKey) : null;

  return (
    <header
      ref={headerRef}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-[60] transition-all ${
        scrolled || activeEntry
          ? "backdrop-blur-xl bg-[color:var(--surface-3)] border-b border-[color:var(--line)]"
          : "bg-transparent"
      }`}
    >
      {/* ============ TOP TIER (DESKTOP) ============
          Slim utility row · 36 px tall when expanded · collapses to 0
          on scroll so only the primary nav stays sticky once the user
          starts engaging with the page. Hidden on mobile (mobile uses
          a single bar + full-screen drawer).
          - LEFT: Prop Trading aqua pill (highlighted sister brand) +
            utility links (Demo Account · Contact Us · Help Center)
          - RIGHT: Language switcher · Theme toggle. The primary CTAs
            (Sign In · Open Account) live in the bottom tier so they
            stay reachable when the top tier collapses on scroll. */}
      <motion.div
        className="hidden lg:block overflow-hidden"
        initial={false}
        animate={{ height: (scrolled && !searchExpanded) ? 0 : 44, opacity: (scrolled && !searchExpanded) ? 0 : 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        data-testid="navbar-top-tier"
        style={{ background: "color-mix(in srgb, var(--surface-3) 70%, transparent)" }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-11">
          <div className="flex items-center gap-6">
            {/* Prop Trading · highlighted aqua pill · sister-brand
                callout that used to be a low-contrast inline link in
                the primary nav. The pill style lifts it out so first-
                time visitors immediately understand we're broker + prop. */}
            <a
              href="https://ofunded.com"
              target="_blank"
              rel="noreferrer"
              data-testid="nav-prop-trading-pill"
              className="group inline-flex items-center gap-2 h-8 pl-3 pr-2.5 rounded-full font-medium text-[12.5px] transition-all"
              style={{
                background: "color-mix(in srgb, var(--brand) 14%, transparent)",
                border: "1px solid color-mix(in srgb, var(--brand) 38%, transparent)",
                color: "var(--brand)",
                boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--brand) 8%, transparent)",
              }}
            >
              {/* tiny pulsing dot · subtle "live" cue */}
              <span className="relative flex h-1.5 w-1.5">
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                  style={{ background: "var(--brand)" }}
                />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "var(--brand)" }} />
              </span>
              {t("nav.propTrading", "Prop Trading")}
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-0.5"
                style={{ background: "color-mix(in srgb, var(--brand) 18%, transparent)" }}
              >
                <ArrowUpRight size={10} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>

            {/* Utility links · subtle, mono-styled */}
            <ul className="flex items-center gap-5">
              {UTILITY_LINKS.map(({ to, external, Icon, labelKey, fallback }) => {
                const linkClass = "inline-flex items-center gap-1.5 text-[12.5px] text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition-colors";
                const label = t(labelKey, fallback);
                const testId = `nav-utility-${fallback.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                return (
                  <li key={labelKey}>
                    {external ? (
                      <a href={to} target="_blank" rel="noreferrer" data-testid={testId} className={linkClass}>
                        <Icon size={13} strokeWidth={1.8} />{label}
                      </a>
                    ) : (
                      <Link to={to} data-testid={testId} {...prefetchProps(to)} className={linkClass}>
                        <Icon size={13} strokeWidth={1.8} />{label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right side of top tier · search + language + theme.
              Search is an inline-expanding pill that opens a dropdown
              right below it (no modal popup) so it can't be blocked by
              popup-blocker extensions. Login / Open Account live in
              the bottom tier so they remain visible once the top tier
              collapses on scroll. */}
          <div className="flex items-center gap-3">
            <InlineSearch openSignal={searchOpenSignal} testIdSuffix="top" onExpandedChange={setSearchExpanded} />
            <LanguageSwitcher variant="desktop" />
            <ThemeToggle />
          </div>
        </div>
      </motion.div>

      {/* ============ BOTTOM TIER (DESKTOP & MOBILE TRIGGER) ============
          Primary navigation row · always sticky · 56 px tall.
          - LEFT: Logo
          - CENTER (desktop): mega-menu triggers
          - RIGHT (desktop): Sign In · Open Account
          - RIGHT (mobile): hamburger + theme + language */}
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-16 relative">
        <Link
          to="/"
          data-testid="logo-home-link"
          className="flex items-center group shrink-0"
        >
          <OFundedLogo className="h-7 lg:h-8 w-auto" />
        </Link>

        {/* Desktop trigger row · CLICK to open. Hover does nothing. */}
        <nav className="hidden lg:flex items-center gap-2">
          {MEGA_MENU_T.map((entry) => {
            const isActive = activeKey === entry.key;
            return (
              <button
                key={entry.key}
                type="button"
                data-testid={`nav-link-${entry.key}`}
                onClick={() => (isActive ? setActiveKey(null) : openSection(entry.key))}
                aria-expanded={isActive}
                aria-haspopup="menu"
                aria-label={`Open ${t(`nav.${entry.key}`, entry.label)} menu`}
                className={`h-10 inline-flex items-center gap-1.5 px-4 text-[14px] rounded-lg transition-colors ${
                  isActive
                    ? "text-[color:var(--text-1)] bg-[color:var(--surface-3)]"
                    : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                }`}
              >
                {t(`nav.${entry.key}`, entry.label)}
                <ChevronDown
                  size={13}
                  className={`transition-transform ${isActive ? "rotate-180" : ""}`}
                />
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5 shrink-0">
          <a
            href={SIGNIN_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="signin-button"
            className="btn-gloss-ghost h-10 min-w-[108px] px-4 whitespace-nowrap inline-flex items-center justify-center text-sm font-medium rounded-[10px] text-[color:var(--text-1)]"
          >
            {t("nav.signIn")}
          </a>
          <a
            href={openAccountHref}
            target={openAccountIsExternal ? "_blank" : undefined}
            rel={openAccountIsExternal ? "noreferrer" : undefined}
            data-testid="register-button"
            className="btn-gloss h-10 min-w-[108px] px-4 whitespace-nowrap inline-flex items-center justify-center text-sm font-semibold rounded-[10px]"
            style={{
              background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
              color: "var(--brand-ink)",
              "--gloss-accent": "var(--brand-deep)",
            }}
          >
            {t("nav.register")}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-1">
          {/* Mobile inline search · same component as desktop top bar.
              Collapsed it's a single icon; tapping it expands to a
              full input with the results dropdown anchored below. */}
          <InlineSearch openSignal={searchOpenSignal} testIdSuffix="mobile" />
          <LanguageSwitcher variant="mobile" />
          <ThemeToggle />
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nav.toggleMenu", "Toggle menu")}
            data-testid="mobile-menu-toggle"
            className="text-[color:var(--text-1)] p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Desktop full-width mega-menu ribbon · opens on CLICK, stays
          open until the user clicks outside the header or presses ESC. */}
      <div className="hidden lg:block">
        <AnimatePresence mode="wait">
          {activeEntry && <MegaRibbon entry={activeEntry} onClose={() => setActiveKey(null)} />}
        </AnimatePresence>
      </div>

      {/* Mobile menu · full-screen overlay extracted into its own
          component for maintainability. Owns no state of its own — the
          parent passes `open` / `onClose` so it stays cleanly decoupled. */}
      <NavbarMobileMenu
        open={open}
        onClose={() => setOpen(false)}
        menuEntries={MEGA_MENU_T}
        signinUrl={SIGNIN_URL}
        openAccountHref={mobileCta.href}
        openAccountIsExternal={mobileCta.isExternal}
      />
    </header>
  );
}
